import React, { useRef, useState } from 'react';

// Click-and-drag sideways scrolling for a strip with its scrollbar hidden (`no-scrollbar`). A touch
// screen swipes it natively, but a mouse has nothing to take hold of once the bar is gone, so a
// desktop at phone width (or any narrow window) could not scroll it at all.
//
// The same approach as the Explore page's NearbyCoursesStrip, which keeps its own copy because it
// also settles onto a card and drives auto select:
// - Mouse only. A touch swipe is already a real scroll, and writing scrollLeft on top of it makes
//   two things move the strip at once.
// - While dragging, the caller should turn scroll snap off (`isDragging`): snap fights a directly
//   assigned scrollLeft and makes the strip jump.
// - The click that ends a drag is swallowed (`onClickCapture`), so letting go over a card does not
//   open it.

const DRAG_THRESHOLD = 3;

interface DragScroll {
  ref: React.RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  handlers: {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
    onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => void;
  };
}

export const useDragScroll = (): DragScroll => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const drag = useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
    dragged: false,
  });

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const el = ref.current;
    if (el == null || event.pointerType !== 'mouse') {
      return;
    }
    drag.current = {
      isDown: true,
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
      dragged: false,
    };
    setIsDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const el = ref.current;
    if (el == null || !drag.current.isDown) {
      return;
    }
    const delta = event.clientX - drag.current.startX;
    if (Math.abs(delta) > DRAG_THRESHOLD) {
      drag.current.dragged = true;
    }
    el.scrollLeft = drag.current.startScrollLeft - delta;
  };

  const endDrag = (): void => {
    if (!drag.current.isDown) {
      return;
    }
    drag.current.isDown = false;
    setIsDragging(false);
  };

  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (drag.current.dragged) {
      event.stopPropagation();
      event.preventDefault();
      drag.current.dragged = false;
    }
  };

  return {
    ref,
    isDragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerLeave: endDrag,
      onClickCapture,
    },
  };
};
